import type { Point } from '@trippath/shared';

/** A thing of a day as it is ordered: where it is, if known, and whether it keeps its place —
 * one with a time set; one with no place keeps its place too. */
export interface Spot {
  key: string;
  point: Point | null;
  fixed: boolean;
}

/** How one goes between a day's places, and how fast in a town on average: a walk, its
 * transport with its waits, a car in its traffic. */
export const speeds = { walk: 4.5, transit: 18, car: 22 } as const;
export type Pace = keyof typeof speeds;

/** A way in a town is about a third longer than the straight line. */
const DETOUR = 1.3;

/** Kilometres between two points along the earth. */
export function distanceKm(a: Point, b: Point): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** About how far a day goes from `start` through each known place to `end`, by its streets. */
export function lengthOf(
  start: Point | null,
  spots: readonly Spot[],
  end: Point | null,
): number {
  const points = [start, ...spots.map((spot) => spot.point), end].filter(
    (point): point is Point => point !== null,
  );
  let km = 0;
  points.reduce<Point | null>((before, point) => {
    if (before) km += distanceKm(before, point) * DETOUR;
    return point;
  }, null);
  return km;
}

/** Minutes for `km` at a pace. */
export function minutesOf(km: number, pace: Pace): number {
  return Math.round((km / speeds[pace]) * 60);
}

const movable = (spot: Spot) => !spot.fixed && spot.point !== null;

/**
 * The day's shortest order from `start` to `end`: any two things that may move swapped, again
 * and again, while that makes the day shorter. A thing with a time set, or with no place, keeps
 * its slot.
 */
export function shortest(
  start: Point | null,
  spots: readonly Spot[],
  end: Point | null,
): Spot[] {
  const now = lengthOf(start, spots, end);
  for (const [i, a] of spots.entries()) {
    for (const [j, b] of spots.entries()) {
      if (j > i && movable(a) && movable(b)) {
        const tried = spots.map((spot, k) => (k === i ? b : k === j ? a : spot));
        if (lengthOf(start, tried, end) < now - 1e-9) return shortest(start, tried, end);
      }
    }
  }
  return [...spots];
}
